import {
	Component,
	OnInit,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectorRef,
	ChangeDetectionStrategy,
	Self,
	Optional
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { IdService, TabTrap } from "../shared/index";

import Locale from "./locale/index";

import makeDateUtils from "./utils/DateUtils";
// import { en, fr } from "./locale";

import * as _moment from "moment";


@Component({
	selector: "rbc-datepicker",
	templateUrl: "./datepicker.component.html",
	styles: [],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent
	implements OnInit, ControlValueAccessor {
	@Input() id: string;
	@Input() selectedDate: Date;
	@Input() format = "dd MMM yyyy";
	@Input() useUtc: boolean;
	@Input() typeable = true;
	@Input() bootstrapStyling: boolean;
	@Input() inputClass: string | Object | [];
	@Input() required = false;
	@Input() showRequired: boolean;
	@Input() placeholder = "";
	@Input() resetTypedDate: Date;
	@Input() disabled: boolean;
	@Input() errorLabel: string;
	@Input() helpText = "";
	@Input() inline: boolean;
	@Input() initialView: string;
	@Input() minimumView = "day";
	@Input() maximumView = "year";
	@Input() openDate;
	@Input() value;
	@Input() label: string;
	@Input() language = "en";
	@Input() fullMonthName = true;
	@Input() clearButton: boolean;
	@Input() clearButtonIcon: string;
	@Input() mondayFirst: boolean;
	@Input() disabledDates: Object;
	@Input() highlighted: Object;
	@Input() calendarClass: string | Object | Array<any>;
	@Input() dayCellContent;
	@Input() name: string;
	@Input() size: "small" | "medium" | "large" = "medium";

	@Input() ariaDescribedby: string;
	@Input() ariaLabel: string;
	@Input() ariaLabelledby: string;
	@Input() ariaInvalid: boolean;

	@Output() selected = new EventEmitter();
	@Output() onInput = new EventEmitter();
	@Output() cleared = new EventEmitter();
	@Output() selectedDisabled = new EventEmitter();
	@Output() changedMonth = new EventEmitter();
	@Output() changedYear = new EventEmitter();
	@Output() closed = new EventEmitter();
	@Output() opened = new EventEmitter();
	@Output() inputTextUpdate = new EventEmitter();
	@Output() onFocusOut = new EventEmitter();

	@ViewChild("container", { static: false }) container: ElementRef;

	@ViewChild("pickerDay", { static: true }) pickerDay: ElementRef;
	@ViewChild("pickerMonth", { static: false }) pickerMonth: ElementRef;
	@ViewChild("pickerYear", { static: false }) pickerYear: ElementRef;

	startDate = this.openDate ? new Date(this.openDate) : new Date();
	showDayView = false;
	showMonthView = false;
	showYearView = false;
	pageTimestamp = makeDateUtils.setDate(this.startDate, 1);

	clickListener = this.clickOutside.bind(this);

	constructor(
		private idService: IdService,
		private tabTrap: TabTrap,
		private _eref: ElementRef,
		private cdr: ChangeDetectorRef,
		@Optional() @Self() public controlDirective: NgControl) {
			this.id = this.id ? this.id : this.idService.generate();
			if(this.controlDirective){
				controlDirective.valueAccessor = this;
			}
		}

	// Function to call when the rating changes.
	onChange = (item) => { };
	// Function to call when the input is touched (when a star is clicked).
	onTouched = () => { };

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	calendarStyle() {
		return {
			position: this.isInline() ? "static" : undefined
		};
	}

	isInline() {
		return !!this.inline;
	}

	isOpen() {
		return this.showDayView || this.showMonthView || this.showYearView;
	}

	computedInitialView() {
		if (!this.initialView) {
			return this.minimumView;
		}

		return this.initialView;
	}

	setInvalid(){
		let flag;
		this.controlDirective ? flag = this.controlDirective.invalid : flag = this.ariaInvalid;
		return flag;
	}

	isRtl() {
		return this.translation().rtl === true;
	}

	dayCalendar() {
		return this.pickerDay["dayContainer"].nativeElement;
	}

	monthCalendar() {
		return this.pickerMonth["monthContainer"].nativeElement;
	}

	yearCalendar() {
		return this.pickerYear["yearContainer"].nativeElement;
	}

	pageDate() {
		return new Date(this.pageTimestamp);
	}

	translation() {
		return this.language === "fr" ? Locale.fr : Locale.en;
	}



	ngOnInit() {
		this.openDate = makeDateUtils.validateDateInput(this.openDate);
		// if (this.value) {
		// 	this.setValue(this.value);
		// }
		// if (this.isInline) {
		// 	this.setInitialView();
		// }
		this.init();
	}

	// Allows Angular to disable the input.
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.detectChanges();
	}

	// Implemented from ControlValueAccessor interface
	writeValue(text: any): void {
		this.selectedDate = text;
		this.value = this.selectedDate;
		this.cdr.detectChanges();
	}

	/**
	 * Called in the event that the user navigates to date pages and
	 * closes the picker without selecting a date.
	 */
	resetDefaultPageDate() {
		if (this.selectedDate === null) {
			this.setPageDate();
			return;
		}
		this.setPageDate(this.selectedDate);
	}

	/**
	 * Effectively a toggle to show/hide the calendar
	 */
	showCalendar(e) {
		const _self = this;
		if (_self.disabled || _self.isInline()) {
			return false;
		}
		if (_self.isOpen()) {
			return _self.close(true);
		}
		_self.setInitialView(e);
		if (!_self.isInline()) {
			document.addEventListener("click", this.clickListener);
			document.addEventListener("keydown", this.clickListener);
			this.opened.emit();
		}
	}

	/**
	 * Sets the initial picker page view: day, month or year
	 */
	setInitialView(e?) {
		const initialView = this.computedInitialView();
		const _self = this;

		switch (initialView) {
			case "year":
				_self.showYearCalendar(e);
				break;
			case "month":
				_self.showMonthCalendar(e);
				break;
			default:
				_self.showDayCalendar(e);
				break;
		}
	}

	/**
	 * Are we allowed to show a specific picker view?
	 */
	allowedToShowView(view) {
		const views = ["day", "month", "year"];
		const minimumViewIndex = views.indexOf(this.minimumView);
		const maximumViewIndex = views.indexOf(this.maximumView);
		const viewIndex = views.indexOf(view);
		return viewIndex >= minimumViewIndex && viewIndex <= maximumViewIndex;
	}

	/**
	 * Show the day picker
	 */
	showDayCalendar(e?) {
		const _self = this;

		if (!this.allowedToShowView("day")) {
			return false;
		}

		this.close();
		this.showDayView = true;
		window.removeEventListener("keydown", function (e) { _self.tabTrap.$_TabTrap_trap(e, _self.monthCalendar()); });
		window.removeEventListener("keydown", function (e) { _self.tabTrap.$_TabTrap_trap(e, _self.yearCalendar()); });
		window.addEventListener("keydown", function (e) { _self.tabTrap.$_TabTrap_trap(e, _self.dayCalendar()); });

		if (typeof e === "undefined" || typeof e.click === "undefined" || e.click !== true) {
			setTimeout(() => {
				_self.dayCalendar().querySelectorAll(".rbc-datepicker-calendar-cell.rbc-datepicker-calendar-cell-day [role=button]")[0].focus();
			});
		}
		return true;
	}

	/**
	 * Show the month picker
	 */
	showMonthCalendar(e?) {
		const _self = this;
		if (!this.allowedToShowView("month")) {
			return false;
		}
		this.close();
		this.showMonthView = true;
		window.removeEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.dayCalendar());
		});
		window.removeEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.yearCalendar());
		});
		window.addEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.monthCalendar());
		});
		if (
			typeof e === "undefined" ||
			typeof e.click === "undefined" ||
			e.click !== true
		) {
			setTimeout(() =>
				_self.monthCalendar()
					.querySelectorAll(
						".rbc-datepicker-calendar-cell.rbc-datepicker-calendar-cell-month[role=button]"
					)[0]
					.focus()
			);
		}
		return true;
	}

	/**
	 * Show the year picker
	 */
	showYearCalendar(e) {
		const _self = this;
		if (!this.allowedToShowView("year")) {
			return false;
		}
		this.close();
		this.showYearView = true;
		window.removeEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.dayCalendar());
		});
		window.removeEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.monthCalendar());
		});
		window.addEventListener("keydown", function (e) {
			_self.tabTrap.$_TabTrap_trap(e, _self.yearCalendar());
		});
		if (
			typeof e === "undefined" ||
			typeof e.click === "undefined" ||
			e.click !== true
		) {
			setTimeout(() =>
				_self.yearCalendar()
					.querySelectorAll(
						".rbc-datepicker-calendar-cell.rbc-datepicker-calendar-cell-year[role=button]"
					)[0]
					.focus()
			);
		}
		return true;
	}

	/**
	 * Set the selected date
	 */
	setDate(timestamp) {
		const date = new Date(timestamp);
		this.selectedDate = date;
		this.setPageDate(date);
		this.selected.emit(date);
		this.onInput.emit(date);
		this.value = this.selectedDate;
		this.onChange(date);
	}

	/**
	 * Clear the selected date
	 */
	clearDate() {
		this.selectedDate = null;
		this.setPageDate(null);
		this.selected.emit(null);
		this.onInput.emit(null);
		this.value = this.selectedDate;
		this.onChange(null);
		this.cleared.emit();
	}


	selectDate(date) {
		this.setDate(date.timestamp);
		if (!this.isInline()) {
			this.close(true);
		}

		this.resetTypedDate = new Date();
	}

	selectDisabledDate(date) {
		this.selectedDisabled.emit(date);
	}

	selectMonth(month) {
		const date = new Date(month.timestamp);
		if (this.allowedToShowView("day")) {
			this.setPageDate(date);
			this.changedMonth.emit(month);
			this.showDayCalendar();
		} else {
			this.setDate(date);
			if (!this.isInline()) {
				this.close(true);
			}
		}
	}


	selectYear(year) {
		const date = new Date(year.timestamp);
		if (this.allowedToShowView("month")) {
			this.setPageDate(date);
			this.changedYear.emit(year);
			this.showMonthCalendar();
		} else {
			this.setDate(date);
			if (!this.isInline()) {
				this.close(true);
			}
		}
	}

	/**
	 * Set the datepicker value
	 */
	setValue(date) {
		if (typeof date === "string" || typeof date === "number") {
			let parsed = new Date(date);
			date = isNaN(parsed.valueOf()) ? null : parsed;
		}
		if (!date) {
			this.setPageDate();
			this.selectedDate = null;
			return;
		}
		this.selectedDate = date;
		this.setPageDate(date);
	}

	/**
	 * Sets the date that the calendar should open on
	 */
	setPageDate(date?) {
		if (!date) {
			if (this.openDate) {
				date = new Date(this.openDate);
			} else {
				date = new Date();
			}
		}
		this.pageTimestamp = makeDateUtils.setDate(new Date(date), 1);
	}

	/**
	 * Handles a month change from the day picker
	 */
	handleChangedMonthFromDayPicker(date) {
		this.setPageDate(date);
		this.changedMonth.emit(date);
	}

	/**
	 * Set the date from a typedDate event
	 */
	setTypedDate(date) {
		this.setDate(date.getTime());
	}

	/**
	 * Close all calendar layers
	 */
	close(emitEvent?) {
		if (!this.isInline()) {
			this.showDayView = this.showMonthView = this.showYearView = false;
			if (emitEvent) {
				this.closed.emit();
				this.cdr.detectChanges();
				document.removeEventListener("click", this.clickListener);
				document.removeEventListener("keydown", this.clickListener);
			}

		}
	}

	/**
	 * Close all calendar layers if click outside
	 */
	clickOutside(e) {
		if (e.key && e.key.toLowerCase().indexOf("esc") !== -1) {
			this.close(true);
		}
		if (!this._eref.nativeElement.contains(e.target)){
			this.close(true);
		}
	}
	/**
		 * Initiate the component
		 */
	init() {
		if (this.value) {
			this.setValue(this.value);
		}
		if (this.isInline()) {
			this.setInitialView();
		}
	}

	onFocusout(e) {
		// check if selected date is disabled
		if (this.selectedDate && this.pickerDay["dayContainer"]) {
			const selectedDay = makeDateUtils.getDate(this.selectedDate);
			// use days data from the picker-day child component
			// const isDisabled = this.$refs.pickerDay.days[selectedDay].isDisabled;
			// if disabled, then clear the input field and model value
			// if (isDisabled) {
			// 	this.clearDate();
			// }
		}

		this.onFocusOut.emit(e);
	}
}
